import { useEffect, useRef, useState } from "react";
import { Button, Dialog, IconButton } from "@mui/material";
import { IoClose } from "react-icons/io5";
import axios from "axios";
import React from "react";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import useAuth from "../../hooks/useAuth";
import useConfirm from "../../hooks/useConfirm";
import useProfile from "../../hooks/useProfile";
import { baseURL } from "../../config/api";
import colorConfigs from "../../config/colorConfigs";
import { PleasewaitPopup } from "../../components/PleasewaitPopup";
import { PhoneNumberInput } from "../../components/PhoneNumberInput";
import PasswordValidation from "../../components/PasswordValidation";
import { formatUSPhone } from "../../utils/phoneFormatter";
import EmailAvailabilityField from "../../components/EmailAvailabilityField";
import { checkEmail } from "../../services/EmailCheck";
import UsernameAvailabilityField from "../../components/UsernameAvailabilityField";
import { checkUsername } from "../../services/CheckUsername";

export const UserProfile = () => {
    const token = Cookies.get("token-velox-charter");
    const userId = Cookies.get("userId-velox-charter");
    const { auth, setAuth }: any = useAuth();
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
    const { profileOpen, setProfileOpen }: any = useProfile();
    const [currUserName, setCurrUserName] = useState<string>("");
    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const [username, setUsername] = useState<string>("");
    const [emailAddress, setEmailAddress] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [isValidPassword, setIsValidPassword] = useState<boolean>(false);
    const [passwordTouched, setPasswordTouched] = useState<boolean>(false);
    const [mobilePhoneNumber, setMobilePhoneNumber] = useState<string>("");
    const [language, setLanguage] = useState<string>("English");
    const [dateFormat, setDateFormat] = useState<string>("");
    const [pleaseWaitPopup, setPleaseWaitPopup] = useState<boolean>(false);
    const [imagePath, setImagePath] = useState<string>("");
    const inputFileRef = useRef<HTMLInputElement>(null);
    const [uploadFileImg, setUploadFileImg] = useState<FileList | null>(null);
    const [imageError, setImageError] = useState("");

    useEffect(() => {
        if (profileOpen) {
            setUploadFileImg(null);
            setPleaseWaitPopup(true)
            axios
                .get(`${baseURL}users/${userId}`)
                .then((response) => {
                    const user = response?.data?.data;
                    setCurrUserName(user?.firstName + " " + user?.lastName);
                    setFirstName(user?.firstName);
                    setLastName(user?.lastName);
                    setUsername(user?.username);
                    setEmailAddress(user?.emailAddress ?? "");
                    setDateFormat(user?.dateFormat);
                    setMobilePhoneNumber(user?.mobilePhoneNumber ?? "");
                    setLanguage(user?.language);
                    setImagePath(user?.profilePic)
                    setPleaseWaitPopup(false);
                })
                .catch((error) => {
                    setPleaseWaitPopup(false);
                });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileOpen]);

    const onFileChangeCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];

        if (!file) return;

        const validTypes = ["image/jpeg", "image/png"];
        const maxSize = 5 * 1024 * 1024;

        if (!validTypes.includes(file.type)) {
            setImageError("Only JPG and PNG images are allowed.");

            if (inputFileRef.current) {
                inputFileRef.current.value = "";
            }

            return;
        }

        if (file.size > maxSize) {
            setImageError("Image size must be less than 5 MB.");

            if (inputFileRef.current) {
                inputFileRef.current.value = "";
            }

            return;
        }

        setImageError("");
        setUploadFileImg(e.target.files);
    };

    const getData = (accessToken: string, uploadedImagePath: string) => {
        const jsonData: any = jwtDecode(accessToken);

        const id = jsonData.sub;
        const roles = jsonData.role;
        const userName = jsonData.username;
        const emailAddress = jsonData.email;

        const expiryDate = new Date(jsonData.exp * 1000);

        Cookies.set("token-velox-charter", accessToken, {
            expires: expiryDate,
        });

        Cookies.set("role-velox-charter", roles ?? "", {
            expires: expiryDate,
        });

        Cookies.set("roleId-velox-charter", Cookies.get("roleId-velox-charter") || "0", {
            expires: expiryDate,
        });

        Cookies.set("userId-velox-charter", id, {
            expires: expiryDate,
        });

        Cookies.set("profileImg-velox-charter", uploadedImagePath ?? "", {
            expires: expiryDate,
        });

        Cookies.set("userName-velox-charter", userName ?? "", {
            expires: expiryDate,
        });

        setAuth({
            id,
            userName,
            givenname: userName,
            emailAddress,
            roles,
            accessToken,
            profileImage: uploadedImagePath,
        });

        setPleaseWaitPopup(false);
        setPassword("");
        setPasswordTouched(false);
        setIsValidPassword(false);
        setImageError("");
        setProfileOpen(false);
    };

    const handleClose = () => {
        setPassword("");
        setPasswordTouched(false);
        setIsValidPassword(false);

        setUploadFileImg(null);
        setImageError("");
        if (inputFileRef.current) {
            inputFileRef.current.value = "";
        }

        setProfileOpen(false);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        setPleaseWaitPopup(true);

        let uploadedImagePath = imagePath;

        try {

            if (uploadFileImg && uploadFileImg.length > 0) {
                const formData = new FormData();
                formData.append("file", uploadFileImg[0]);

                const uploadRes = await axios.post(
                    `${baseURL}common-files/upload`,
                    formData,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                        withCredentials: false,
                    }
                );

                uploadedImagePath = uploadRes.data.data.name;
                setImagePath(uploadedImagePath);
            }

            const payload = {
                username,
                emailAddress,
                firstName,
                lastName,
                phoneNumber: "",
                mobilePhoneNumber: formatUSPhone(mobilePhoneNumber),
                dateFormat,
                language,
                profilePic: uploadedImagePath,
                isActive: true,
                updatedBy: auth?.givenname,
                ...(password && { password })
            };

            const response = await axios.put(
                `${baseURL}auth/update-profile`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    withCredentials: false,
                }
            );

            if (response.data?.success) {
                getData(response.data.data.access_token, uploadedImagePath);
            } else {
                throw new Error(response.data?.message || "Profile update failed.");
            }
        } catch (error: any) {
            const errorMessage =
                error?.response?.data?.message ||
                error?.response?.data ||
                error?.message ||
                "Profile update failed.";

            setConfirmData({
                title: "Warning",
                summary: errorMessage,
                confirmBtn: "Ok",
                isConfirm: (callback: any) => callback(),
            });

            setConfirmPopup(true);
            setPleaseWaitPopup(false);
        }
    };

    return (
        <>
            <Dialog
                open={profileOpen}
                sx={{
                    '& .MuiDialog-paper': {
                        maxWidth: '800px',
                        width: '100%',
                        borderRadius: '16px',
                    },
                }}
            >
                <div className={`px-[24px] py-[10px] h-full`}>
                    <div className={`w-full bg-transparent rounded-lg p-[10px] pb-[40px] flex flex-col gap-2.51`}>
                        <div className="w-full mt-2">
                            <div className={`w-full pb-4`}>
                                <div className="flex justify-between items-center border-b pb-3">
                                    <h2>Profile - {currUserName}</h2>

                                    <IconButton onClick={handleClose}>
                                        <IoClose size={20} />
                                    </IconButton>
                                </div>

                                <div className="mt-2 p-2">
                                    <form onSubmit={(e) => handleSubmit(e)}>
                                        <div className="w-full">
                                            <div className="flex items-center gap-5 mt-4">
                                                <div className="relative">
                                                    <img
                                                        className="w-28 h-28 rounded-full object-cover border-4 border-white shadow-lg"
                                                        alt="Profile"
                                                        src={
                                                            uploadFileImg?.length
                                                                ? URL.createObjectURL(uploadFileImg[0])
                                                                : imagePath
                                                                    ? `${baseURL}common-files/get/${imagePath}`
                                                                    : "/images/profile-placeholder.png"
                                                        }
                                                        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                                            e.currentTarget.src = "/images/profile-placeholder.png";
                                                        }}
                                                    />

                                                    <label
                                                        htmlFor="profile-upload"
                                                        className="absolute bottom-0 right-0 w-9 h-9 rounded-full bg-[#167AC3] text-white flex items-center justify-center cursor-pointer shadow-lg hover:bg-[#0F65A5] transition-all"
                                                    >
                                                        ✏️
                                                    </label>
                                                </div>

                                                <div>
                                                    <input
                                                        id="profile-upload"
                                                        ref={inputFileRef}
                                                        type="file"
                                                        accept="image/*"
                                                        onChangeCapture={onFileChangeCapture}
                                                        className="hidden"
                                                    />
                                                    {imageError && (
                                                        <p className="mt-2 text-xs text-red-500">
                                                            {imageError}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-2 gap-6 my-2.5">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                                                    <input
                                                        type="text"
                                                        className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                        value={firstName}
                                                        placeholder="Enter First Name"
                                                        onChange={(e) => setFirstName(e.target.value)}
                                                        required
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                                                    <input
                                                        type="text"
                                                        placeholder="Enter first name"
                                                        className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                        value={lastName}
                                                        onChange={(e) => setLastName(e.target.value)}
                                                        required
                                                    />
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-2 gap-6 my-2.5">
                                                <div>
                                                    <UsernameAvailabilityField
                                                        username={username}
                                                        setUsername={setUsername}
                                                        getUsernameChecking={checkUsername}
                                                        label="Username"
                                                        placeholder="Enter Username"
                                                        required
                                                    />
                                                </div>

                                                <div>
                                                    <PasswordValidation
                                                        password={password}
                                                        setPassword={setPassword}
                                                        isValid={isValidPassword}
                                                        setIsValid={setIsValidPassword}
                                                        touched={passwordTouched}
                                                        setTouched={setPasswordTouched}
                                                        className={{
                                                            parentDiv: "relative",
                                                            label: "block text-sm font-medium text-gray-700 mb-2",
                                                            input:
                                                                "w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary",
                                                        }}
                                                    />
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-2 gap-6 my-2.5">
                                                <div>
                                                    <EmailAvailabilityField
                                                        emailAddress={emailAddress}
                                                        setEmailAddress={setEmailAddress}
                                                        getEmailChecking={checkEmail}
                                                        label="Email Address"
                                                        placeholder="Enter email address"
                                                        required
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Number</label>
                                                    <PhoneNumberInput
                                                        name={"phoneNumber"}
                                                        value={mobilePhoneNumber}
                                                        setValue={setMobilePhoneNumber}
                                                        fontSize={"14px"}
                                                        required={true}
                                                    />
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-2 gap-6 my-2.5">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">Date Format</label>
                                                    <select
                                                        className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                        value={dateFormat}

                                                        onChange={(e) => setDateFormat(e.target.value)}
                                                    >
                                                        <option value="MM/dd/yyyy KK:mm a">Use System Default</option>
                                                        <option value="yyyy-MM-dd kk:mm">2023-06-16 7:37 ()</option>
                                                        <option value="yyyy-MM-dd kk:mm iso">2023-06-16 7:37 (iso)</option>
                                                        <option value="dd-MM-yyyy kk:mm">16-06-2023 7:37 (eu)</option>
                                                        <option value="MM/dd/yyyy KK:mm a us">06/16/2023 7:37 AM (us)</option>
                                                        <option value="dd/MM/yyyy KK:mm a">16/06/2023 7:37 AM (ve)</option>
                                                        <option value="dd.MM.yyyy KK:mm a">16.06.2023 7:37 AM (int)</option>
                                                        <option value="dd-MMMM-yyyy KK:mm a">16-Jun-2023 7:37 AM (id)</option>
                                                        <option value="dd.MM.yyyy kk:mm">16.06.2023 7:37 (ch)</option>
                                                    </select>
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
                                                    <select
                                                        className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                                        value={language}
                                                        onChange={(e) => setLanguage(e.target.value)}
                                                    >
                                                        <option value="English">English</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="flex gap-4 mt-6">

                                                <Button variant="contained"
                                                    sx={{
                                                        backgroundColor: colorConfigs.btn.main.bg
                                                    }}
                                                    type="submit"
                                                >
                                                    Save
                                                </Button>


                                                <Button variant="contained"
                                                    sx={{
                                                        backgroundColor: colorConfigs.btn.cancel.bg,
                                                        border: colorConfigs.btn.cancel.border,
                                                        color: colorConfigs.btn.cancel.color
                                                    }}
                                                    onClick={handleClose}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div >
            </Dialog >

            <PleasewaitPopup showPopup={pleaseWaitPopup} />
        </>
    )
}